import React, {Fragment, useCallback, useEffect, useState, useContext} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import _, {isNil} from 'lodash';
import {GetHelixProjectFromCanvas} from '../../../../actions/HelixIntegration/engagementhelixaction';
import * as MaterialityAction from '../../../../actions/Materiality/materialityactions';
import {clearFormBodySaveStateById} from '../../../../actions/Form/formBodyActions';
import {getCurrentMateriality} from '../../../../selectors/Ares/materiality';
import {materialityTypes, helixBasisAmount, trialBalanceCategories} from '../../../../util/uiconstants';
import {isNullOrUndefined, labels} from '../../../../util/utils';
import MeasurementBasisAnnualizedAmountContainer from './MeasurementBasisAnnualizedAmountContainer';
import MeasurementBasisFinancialPeriod from './MeasurementBasisFinancialPeriod';
import MeasurementBasisRationale from './MeasurementBasisRationale';
import MeasurementBasisContainer from './MeasurementBasisContainer';
import {FormBodyChangesContext} from '../../CanvasForm/Bodies/FormBodyChangesWatcher';
import MeasurementBasisMessage from './MeasurementBasisMessage';
import {useEngagementId} from '../../../../util/customHooks';
import MaterialityFetch from '../MaterialityFetch';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import {useSelectedStEntityId} from '../../../../util/Ares/customHooks';

function MeasurementBasis({bodyId, isAresView, documentId, isDisabled}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);
	const materiality = useSelector((store) => getCurrentMateriality(store), shallowEqual);
	const [currentMateriality, setCurrentMateriality] = useState(null);
	const [financialPeriodAmount, setFinancialPeriodAmount] = useState(null);
	const [isCurrentAnalysisDateAmountValuePresent, setIsCurrentAnalysisDateAmountValuePresent] = useState(false);
	const [isRadioChecked, setIsRadioChecked] = useState(null);
	const [lastFinancialPeriodAmountSaved, setLastFinancialPeriodAmountSaved] = useState(null);
	const [lastRationaleSaved, setLastRationaleSaved] = useState('');
	const [primaryHelixProject, setPrimaryHelixProject] = useState(null);
	const [rationale, setRationale] = useState('');
	const [showOverlay, setShowOverlay] = useState(false);

	const {resetChanges, onBodyValueChanged} = useContext(FormBodyChangesContext);
	const [currentStEntityId] = useSelectedStEntityId();

	useEffect(() => {
		if (!_.isEmpty(materiality)) {
			const currentMateriality = materiality;
			setCurrentMateriality(currentMateriality);

			if (currentMateriality?.helixModel !== null && currentMateriality?.helixModel?.basisAmount !== null) {
				setIsCurrentAnalysisDateAmountValuePresent(true);
				setIsRadioChecked(currentMateriality?.helixModel?.isAnnualizedAmountExpectedToBeReported);
			} else {
				setIsCurrentAnalysisDateAmountValuePresent(false);
				setIsRadioChecked(false);
			}

			setFinancialPeriodAmount(currentMateriality.basisForecastAmount);
			setLastFinancialPeriodAmountSaved(currentMateriality.basisForecastAmount);
			setRationale(currentMateriality.basisForecastAmountRationale);
			setLastRationaleSaved(currentMateriality.basisForecastAmountRationale);
		}
	}, [materiality]);

	useEffect(() => {
		if (engagementId) {
			const query = {
				trialBalanceTypeCategory: trialBalanceCategories.Planning,
				IncludeConnections: true
			};

			if (currentStEntityId) {
				query.multiEntityIds = currentStEntityId;
			}

			dispatch(GetHelixProjectFromCanvas(engagementId, query));
		}
	}, [engagementId, dispatch]);

	useEffect(() => {
		if (engagementHelixProjects) {
			let helixConnection = engagementHelixProjects.find((x) => x.isPrimary);
			if (helixConnection) {
				setPrimaryHelixProject(helixConnection);
			}
		}
	}, [engagementHelixProjects]);

	const updateMateriality = useCallback(
		async (
			basisForecastAmount,
			basisForecastAmountRationale,
			isAnnualizedAmountExpectedToBeReported,
			elementName = null
		) => {
			if (!_.isEmpty(currentMateriality)) {
				const updateBody = {
					basisForecastAmount: Math.trunc(Number(basisForecastAmount)),
					basisForecastAmountRationale: basisForecastAmountRationale ? basisForecastAmountRationale : null,
					helixModel: {
						isAnnualizedAmountExpectedToBeReported:
							isAnnualizedAmountExpectedToBeReported === null ? false : isAnnualizedAmountExpectedToBeReported
					}
				};

				setShowOverlay(true);

				// If no elementName is provided, marking the whole body as saved. Otherwise, we just need to mark the elementName as saved
				await dispatch(
					MaterialityAction.updateMateriality(Number(engagementId), currentMateriality.id, updateBody)
				).then(() => {
					setShowOverlay(false);
					elementName ? resetChanges(bodyId, elementName) : dispatch(clearFormBodySaveStateById(bodyId));
				});
			}
		},
		[currentMateriality, dispatch, engagementId, resetChanges]
	);

	const onChange = (elementName, initialValue, newValue) => {
		onBodyValueChanged(bodyId, initialValue, newValue, elementName);
	};

	const onCancel = (elementName) => {
		resetChanges(bodyId, elementName);
	};

	const setAnnualizedAmountTitle = () => {
		return currentMateriality?.materialityTypeID === materialityTypes.Equity ||
			currentMateriality?.materialityTypeID === materialityTypes.Assets ||
			currentMateriality?.materialityTypeID === materialityTypes.CapitalBasedOther
			? labels.isAnnualizedAmountRepresentativeForAssetsOrEquity
			: labels.isAnnualizedAmountRepresentative;
	};

	return (
		<MaterialityFetch documentId={documentId}>
			<StyledMeasurementBasis className="measurement-basis customScrollbar">
				<StyledMeasurementBasisWrapper
					className={`StyledMeasurementBasisWrapper aresSectionBodyData ${showOverlay ? 'showOverlayVisible' : ''}`}
				>
					{showOverlay && (
						// <section className="motif-progress-wrapper">
						// 	<ProgressBar isIndeterminate showLabel labelComponent={<span>{labels.saving}</span>} />
						// </section>
						<LoadingIndicator show={showOverlay} view="fullview" styleName={'StyledMeasurementBasisLoader'} />
					)}
					<MeasurementBasisContainer
						currentMateriality={currentMateriality}
						isCurrentAnalysisDateAmountValuePresent={isCurrentAnalysisDateAmountValuePresent}
						primaryHelixProject={primaryHelixProject}
						isDisabled={isDisabled}
					/>
					{currentMateriality?.helixModel?.basisAmount === helixBasisAmount.DEFAULT ? (
						<MeasurementBasisMessage />
					) : (
						<>
							{((isCurrentAnalysisDateAmountValuePresent &&
								primaryHelixProject?.helixJsonModel?.analysisDateElapsedMonthCount <
									primaryHelixProject?.helixJsonModel?.currentYearTotalMonthCount) ||
								(engagementHelixProjects.length > 0 &&
									typeof engagementHelixProjects[0].isLoadedAvailableHelixProjects !== undefined &&
									!isNullOrUndefined(currentMateriality?.helixModel?.elapsedPeriods) &&
									currentMateriality?.helixModel?.elapsedPeriods < currentMateriality?.helixModel?.totalPeriods &&
									!isNullOrUndefined(currentMateriality?.helixModel?.basisAmount))) && (
								<MeasurementBasisAnnualizedAmountContainer
									annualizedAmountTitle={setAnnualizedAmountTitle()}
									currentMateriality={currentMateriality}
									isRadioChecked={isRadioChecked}
									rationale={rationale}
									setIsRadioChecked={setIsRadioChecked}
									updateMateriality={updateMateriality}
									isDisabled={isDisabled}
								/>
							)}
							{isRadioChecked === false && (
								<Fragment>
									<MeasurementBasisFinancialPeriod
										financialPeriodAmount={financialPeriodAmount}
										isAnnualizedAmountExpectedToBeReported={
											currentMateriality?.helixModel?.isAnnualizedAmountExpectedToBeReported
										}
										lastFinancialPeriodAmountSaved={lastFinancialPeriodAmountSaved}
										rationale={rationale}
										setFinancialPeriodAmount={setFinancialPeriodAmount}
										setLastFinancialPeriodAmountSaved={setLastFinancialPeriodAmountSaved}
										updateMateriality={updateMateriality}
										onChange={onChange}
										onCancel={onCancel}
										isDisabled={isDisabled}
									/>
									<MeasurementBasisRationale
										financialPeriodAmount={financialPeriodAmount}
										isAnnualizedAmountExpectedToBeReported={
											currentMateriality?.helixModel?.isAnnualizedAmountExpectedToBeReported
										}
										lastRationaleSaved={lastRationaleSaved}
										rationale={rationale}
										setLastRationaleSaved={setLastRationaleSaved}
										setRationale={setRationale}
										updateMateriality={updateMateriality}
										onChange={onChange}
										onCancel={onCancel}
										isAresView={isAresView}
										isDisabled={isDisabled}
									/>
								</Fragment>
							)}
						</>
					)}
				</StyledMeasurementBasisWrapper>
			</StyledMeasurementBasis>
		</MaterialityFetch>
	);
}

MeasurementBasis.propTypes = {};

export default MeasurementBasis;

const StyledMeasurementBasisWrapper = styled.section`
	width: 100%;
	margin-bottom: var(--px-20);
	padding: var(--px-20) var(--px-15) var(--px-15) var(--px-15);
	border-radius: var(--px-4);
	border: var(--px-1) solid var(--neutrals-200);
	border-left: var(--px-4) solid var(--green-500);
	font-size: var(--px-12);
	background: var(--neutrals-00white);
	&.error {
		border-left: var(--px-4) solid var(--red-600);
	}
	&.aresSectionBodyData {
		position: relative;
		margin-top: var(--px-24);
		padding: var(--px-24);
		border-radius: var(--px-12) !important;
		border: var(--px-1) solid var(--neutrals-200);
		background-color: var(--neutrals-00white);
	}
	&.showOverlayVisible {
		.measurementbasis-container,
		.financialperiod-container,
		.rationale-container {
			opacity: 0.5;
		}
	}
`;

const StyledMeasurementBasisTitle = styled.span`
	display: block;
	font-size: var(--px-16);
	font-weight: 300;
	margin-bottom: var(--px-20);
`;

const StyledMeasurementBasis = styled.section`
	padding: var(--px-30) var(--px-155);
	height: 100%;
	.StyledMeasurementBasisLoader {
		position: absolute;
		left: 50%;
		top: 40%;
	}
	.motif-loader-container {
		&.motif-loader-fullscreen {
			width: 100% !important;
		}
	}
	.motif-progress-loader-container {
		&.motif-progress-loader-fullscreen {
			width: 100% !important;
		}
	}
	.motif-progress-wrapper {
		display: flex;
		.motif-progress-bar-wrapper {
			width: 87.5%;
			height: 34%;
			@media only screen and (width: 1280px) {
				width: 85.5%;
				height: 43%;
			}
		}
	}
`;
